import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LeadStatus } from '@conversia/shared-types';
import * as carteraApi from '@/api/cartera.api';
import type { Cartera, FilaDeCartera } from '@/api/cartera.api';
import * as contactsApi from '@/api/contacts.api';
import * as conversationsApi from '@/api/conversations.api';
import { legible } from '@/lib/cartera';
import { CERRADO } from '@/lib/proceso';
import { CONFIRMAR_CIERRE, SIN_CHAT_NO_CIERRA, conCampo, conEtapa, conTemperatura, resumenDeLote, type Asignables, type ResultadoDeLote } from '@/lib/tablero-clientes';
import { useAsignacion } from './useAsignacion';
import { useToast } from './useToast';

/**
 * EDITAR A LOS CLIENTES DESDE LA LISTA (tanda 6): la etapa, la temperatura, un campo de la
 * ficha y a quién está asignado, de uno en uno (la celda de la tabla) o varios a la vez (las
 * filas marcadas). También sirve al kanban: arrastrar una tarjeta es `cambiarEtapa`.
 *
 * Todo cambio se ve al instante en la caché `['cartera']`; si el servidor lo rechaza, la fila
 * vuelve a como estaba y se avisa con SU mensaje (él sabe por qué: «ya está cerrado»…).
 */
export interface EdicionDeClientes {
  /** A quién se le puede dar un chat, y quién soy yo. */
  asignables: Asignables;
  /** Hay una asignación o un lote en camino: esos desplegables esperan. */
  ocupado: boolean;
  /** `etapa`: una de las configuradas, o `cerrado` (pide confirmar: crea los borradores de postventa). */
  cambiarEtapa: (fila: FilaDeCartera, etapa: string) => void;
  /** `null` = sin clasificar. Pasar a CERRADO pide la misma confirmación. */
  cambiarTemperatura: (fila: FilaDeCartera, temperatura: string | null) => void;
  /** La promesa falla si no se guardó: el editor la usa para volver a mostrar lo que había. */
  guardarCampo: (fila: FilaDeCartera, clave: string, valor: string) => Promise<unknown>;
  /** `valor`: `agente:<id>`, `yo` o `persona:<id>`. */
  asignar: (fila: FilaDeCartera, valor: string) => void;
  etapaEnLote: (filas: FilaDeCartera[], etapa: string) => void;
  asignarEnLote: (filas: FilaDeCartera[], valor: string) => void;
  /** T14: les pone esa fuente a todos (una sola llamada). */
  fuenteEnLote: (filas: FilaDeCartera[], fuente: string) => void;
}

interface Edicion {
  fila: FilaDeCartera;
  optimista: (f: FilaDeCartera) => FilaDeCartera;
  guardar: () => Promise<unknown>;
  /** El título del aviso si falla; debajo va el motivo que dio el servidor. */
  error: string;
}

const LLAVE = ['cartera'] as const;
const EDITAR = ['cartera', 'editar'] as const;
const CONFIRMAR_CIERRE_DE_VARIOS = 'Marcarlos como cerrados crea los borradores de postventa de cada uno. ¿Seguimos?';

const motivoDe = (e: unknown) => (e instanceof Error && e.message ? e.message : 'El servidor no lo aceptó.');
const estaCerrado = (f: FilaDeCartera) => f.conversacion?.temperatura === 'CERRADO';

export function useEdicionDeClientes({ activo = true }: { activo?: boolean } = {}): EdicionDeClientes {
  const qc = useQueryClient();
  const toast = useToast();
  const asignacion = useAsignacion({ activo });
  const [enLote, setEnLote] = useState(false);

  /** Cambia UNA fila de la lista en la caché. */
  const poner = (contactId: string, cambio: (f: FilaDeCartera) => FilaDeCartera) =>
    qc.setQueryData<Cartera>(LLAVE, (c) => c && { ...c, filas: c.filas.map((f) => (f.contactId === contactId ? cambio(f) : f)) });

  const editar = useMutation({
    mutationKey: EDITAR,
    mutationFn: (e: Edicion) => e.guardar(),
    onMutate: async ({ fila, optimista }) => {
      await qc.cancelQueries({ queryKey: LLAVE });
      // La fila como está AHORA en la caché (puede traer otra edición en camino), no la del clic.
      const antes = qc.getQueryData<Cartera>(LLAVE)?.filas.find((f) => f.contactId === fila.contactId);
      poner(fila.contactId, optimista);
      return { antes };
    },
    onSuccess: (respuesta, { fila }) => {
      // Cambiar la etapa devuelve la fila ya con sus efectos (el sello de desde cuándo, la cita cerrada…).
      const nueva = (respuesta as { fila?: FilaDeCartera } | null)?.fila;
      if (nueva) poner(fila.contactId, () => nueva);
    },
    onError: (e, { fila, error }, contexto) => {
      if (contexto?.antes) poner(fila.contactId, () => contexto.antes!);
      toast.error(error, motivoDe(e));
    },
    onSettled: (_r, _e, { fila }) => {
      // Con varias ediciones seguidas, se vuelve a pedir la lista una vez, al terminar la última:
      // pedirla antes pisaría con datos viejos lo que todavía está en camino.
      if (qc.isMutating({ mutationKey: EDITAR }) <= 1) void qc.invalidateQueries({ queryKey: LLAVE });
      void qc.invalidateQueries({ queryKey: ['contact', fila.contactId] });
      if (fila.conversacion) void qc.invalidateQueries({ queryKey: ['conversation', fila.conversacion.id] });
    },
  });

  /** Un lote: corre, y pase lo que pase vuelve a pedir la lista y suelta los desplegables. */
  const lote = async (correr: () => Promise<ResultadoDeLote>, hecho: (n: number) => string) => {
    setEnLote(true);
    try {
      const { tono, titulo, detalle } = resumenDeLote(await correr(), hecho);
      toast[tono](titulo, detalle);
    } finally {
      void qc.invalidateQueries({ queryKey: LLAVE });
      setEnLote(false);
    }
  };

  return {
    asignables: { agentes: asignacion.agentes, personas: asignacion.personas, yoId: asignacion.yoId },
    ocupado: asignacion.ocupado || enLote,

    cambiarEtapa: (fila, etapa) => {
      if (etapa === CERRADO) {
        // El servidor diría lo mismo, pero después de haber preguntado: mejor antes.
        if (!fila.conversacion) return void toast.warning(SIN_CHAT_NO_CIERRA);
        if (!window.confirm(CONFIRMAR_CIERRE)) return;
      }
      editar.mutate({
        fila,
        optimista: (f) => conEtapa(f, etapa),
        guardar: () => carteraApi.cambiarEtapa(fila.contactId, etapa),
        error: 'No se pudo cambiar la etapa',
      });
    },

    cambiarTemperatura: (fila, temperatura) => {
      const conv = fila.conversacion;
      if (!conv) return;
      if (temperatura === 'CERRADO' && !window.confirm(CONFIRMAR_CIERRE)) return;
      editar.mutate({
        fila,
        optimista: (f) => conTemperatura(f, temperatura),
        guardar: () => conversationsApi.updateLeadStatus(conv.id, temperatura as LeadStatus | null),
        error: 'No se pudo cambiar la temperatura',
      });
    },

    guardarCampo: (fila, clave, valor) =>
      editar.mutateAsync({
        fila,
        optimista: (f) => conCampo(f, clave, valor),
        guardar: () => contactsApi.updateContact(fila.contactId, { profileData: { [clave]: valor } }),
        error: 'No se pudo guardar el dato',
      }),

    asignar: asignacion.asignar,

    etapaEnLote: (filas, etapa) => {
      const nombre = legible(etapa);
      // Las que ya están ahí no se tocan ni se cuentan (misma regla que el servidor).
      const porMover = filas.filter((f) => (etapa === CERRADO ? !estaCerrado(f) : f.etapa !== etapa || estaCerrado(f)));
      if (porMover.length === 0) return void toast.info(filas.length === 1 ? `Ya estaba en «${nombre}».` : `Ya estaban en «${nombre}».`);
      if (etapa === CERRADO && !window.confirm(CONFIRMAR_CIERRE_DE_VARIOS)) return;
      void lote(
        async () => {
          // ponytail: N llamadas seguidas; una ruta en lote si se marcan cientos.
          const r: ResultadoDeLote = { hechas: 0, fallos: [] };
          await qc.cancelQueries({ queryKey: LLAVE });
          for (const fila of porMover) {
            poner(fila.contactId, (f) => conEtapa(f, etapa));
            try {
              const { fila: nueva } = await carteraApi.cambiarEtapa(fila.contactId, etapa);
              poner(fila.contactId, () => nueva);
              r.hechas += 1;
            } catch (e) {
              poner(fila.contactId, () => fila);
              r.fallos.push({ nombre: fila.nombre, motivo: motivoDe(e) });
            }
          }
          return r;
        },
        (n) => (n === 1 ? `1 cliente pasó a «${nombre}».` : `${n} clientes pasaron a «${nombre}».`),
      );
    },

    fuenteEnLote: (filas, fuente) => {
      const nombre = fuente.trim();
      void lote(
        async () => {
          try {
            const { cambiados } = await carteraApi.cambiarFuente(
              filas.map((f) => f.contactId),
              nombre,
            );
            return { hechas: cambiados, fallos: [] };
          } catch (e) {
            return { hechas: 0, fallos: filas.map((f) => ({ nombre: f.nombre, motivo: motivoDe(e) })) };
          }
        },
        (n) => (n === 1 ? `1 cliente quedó en «${nombre}».` : `${n} clientes quedaron en «${nombre}».`),
      );
    },

    asignarEnLote: (filas, valor) => {
      void lote(
        () => asignacion.asignarVarios(filas, valor),
        (n) => (n === 1 ? '1 chat asignado.' : `${n} chats asignados.`),
      );
    },
  };
}
